import type { SupabaseClient } from "@supabase/supabase-js";

export type LeadConversationForChat = {
  conversation_id: string;
  channel: string;
  lead_id: string;
  lead_name: string;
  lead_phone: string | null;
  state: string;
  followup_enabled: boolean;
  integration_id: string | null;
  metadata: unknown;
  is_closed: boolean;
};

const CONVERSATION_SELECT =
  "id, channel, lead_id, integration_id, external_user_id, state, followup_enabled, metadata, is_closed";

/**
 * Carrega apenas a conversa mais recente do lead no workspace (sem inbox/lista).
 */
export async function fetchLeadConversationForChat(
  supabase: SupabaseClient,
  workspaceId: string,
  leadId: string,
  leadFallback?: { name?: string; phone?: string | null; metadata?: unknown },
): Promise<LeadConversationForChat | null> {
  const { data: conv, error } = await supabase
    .from("conversations")
    .select(CONVERSATION_SELECT)
    .eq("lead_id", leadId)
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!conv) return null;

  const convRow = conv as {
    id: string;
    channel: string;
    lead_id: string;
    integration_id: string | null;
    state: string | null;
    followup_enabled: boolean | null;
    metadata: unknown;
    is_closed: boolean | null;
  };

  let leadName = leadFallback?.name ?? "Lead";
  let leadPhone = leadFallback?.phone ?? null;
  let leadMetadata = leadFallback?.metadata;

  if (!leadFallback?.name) {
    const { data: lead } = await supabase
      .from("leads")
      .select("name, phone, metadata")
      .eq("id", leadId)
      .maybeSingle();
    if (lead) {
      leadName = lead.name ?? leadName;
      leadPhone = lead.phone ?? leadPhone;
      leadMetadata = lead.metadata ?? leadMetadata;
    }
  }

  return {
    conversation_id: convRow.id,
    channel: convRow.channel,
    lead_id: convRow.lead_id || leadId,
    lead_name: leadName,
    lead_phone: leadPhone,
    state: convRow.state || "active",
    followup_enabled: convRow.followup_enabled ?? true,
    integration_id: convRow.integration_id ?? null,
    metadata: leadMetadata ?? convRow.metadata,
    is_closed: convRow.is_closed ?? false,
  };
}
